import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEnglishPath } from '../../pages/en/enCommon';

const MENUS = [
  { label: '회사소개', path: '/introduce', icon: <ApartmentIcon /> },
  { label: '버스안내', path: '/bus', icon: <DirectionsBusIcon /> },
  { label: '견적목록', path: '/list/1', icon: <ListAltIcon /> },
  { label: '나의견적확인', path: '/my', icon: <ContentPasteSearchIcon /> },
  { label: '고객센터', path: '/center', icon: <SupportAgentIcon /> },
];

/* 영문 홈페이지 메뉴 (2026-11-04). 견적목록·나의견적은 한국 휴대폰 본인확인이 필요해 영어판에는 없다 */
const EN_MENUS = [
  { label: 'About us', path: '/en/introduce', icon: <ApartmentIcon /> },
  { label: 'Our buses', path: '/en/bus', icon: <DirectionsBusIcon /> },
  { label: 'Get a quote', path: '/en/center', icon: <SupportAgentIcon /> },
];

/** PC는 가로 메뉴, 모바일은 햄버거 드로어. 메뉴 구성은 동일하다. */
export default function AppHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const english = isEnglishPath(pathname);
  const menus = english ? EN_MENUS : MENUS;
  // 언어 바꾸기 — 같은 화면의 다른 언어 주소로(없으면 첫 화면)
  const other = english
    ? { label: '한국어', path: { '/en/bus': '/bus', '/en/introduce': '/introduce', '/en/center': '/center' }[pathname] || '/' }
    : { label: 'English', path: { '/bus': '/en/bus', '/introduce': '/en/introduce', '/center': '/en/center' }[pathname] || '/en' };

  const go = (path) => {
    setDrawer(false);
    navigate(path);
  };

  return (
    <>
      <AppBar
        position='sticky'
        elevation={2}
        sx={{ backgroundColor: '#FCFCFC', borderTop: '4px solid #EC9F46', color: '#5A4231' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 60, md: 76 } }}>
          <ButtonBase onClick={() => go(english ? '/en' : '/')} sx={{ p: 0.5 }}>
            <Box
              component='img'
              /* 관리자 화면에서 로고를 바꾸면 즉시 반영되도록 업로드 경로를 사용한다
                 (이전 /image/logo.png 는 24시간 캐시가 걸려 교체가 늦게 반영됐음) */
              src='/uploads/logo.png'
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = '/image/logo.png';
              }}
              alt={english ? 'EK Hana Tour — charter bus in Korea' : '이케이하나관광 버스대절'}
              sx={{ width: { xs: 150, md: 200 }, height: 'auto' }}
            />
          </ButtonBase>

          {/* PC 메뉴 */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {menus.map((menu) => (
              <Button
                key={menu.path}
                onClick={() => go(menu.path)}
                sx={{ color: '#5A4231', fontSize: 16, fontWeight: 700 }}
              >
                {menu.label}
              </Button>
            ))}
            <Button
              component='a'
              href={other.path}
              hrefLang={english ? 'ko' : 'en'}
              onClick={(e) => { e.preventDefault(); go(other.path); }}
              sx={{ color: '#EC9F46', fontSize: 15, fontWeight: 700 }}
            >
              🌐 {other.label}
            </Button>
          </Box>

          {/* 모바일 메뉴 버튼 · 언어 바꾸기(눈에 띄게 밖에 둔다) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <Button
            component='a'
            href={other.path}
            hrefLang={english ? 'ko' : 'en'}
            onClick={(e) => { e.preventDefault(); go(other.path); }}
            sx={{ color: '#EC9F46', fontSize: 13, fontWeight: 700, minWidth: 0, px: 1 }}
          >
            🌐 {english ? 'KO' : 'EN'}
          </Button>
          <IconButton
            onClick={() => setDrawer(true)}
            aria-label={english ? 'Open menu' : '메뉴 열기'}
            sx={{ color: '#5A4231' }}
          >
            <MenuIcon />
          </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor='right' open={drawer} onClose={() => setDrawer(false)}>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={() => setDrawer(false)} aria-label={english ? 'Close menu' : '메뉴 닫기'}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            {menus.map((menu) => (
              <ListItem key={menu.path} disablePadding>
                <ListItemButton onClick={() => go(menu.path)}>
                  <ListItemIcon sx={{ color: '#EC9F46' }}>{menu.icon}</ListItemIcon>
                  <ListItemText primary={menu.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
